import axios from '@/utils/request';
import { OrderItem } from '@/pages/im/types/order';
import { Option, UserInfo } from '@/pages/im/types/option';
import { EvaluateConfig } from '@/pages/im/types/evaluate';
import { HistoryMessageContent, SensitiveCheckResult } from '@/pages/im/types/message';
import { AsrTrackInfo } from '@/pages/im/types/audio';
import { AiConfig, EventReportInfo, GreyInfo } from '@/pages/im/types';

export interface IRes<T> {
  data: T;
  msg?: string;
  ret: number;
}

export interface IConfigInfo {
  sysNum?: string;
  source?: string;
  channelFlag?: string;
  partnerId?: string;
  noticeReq?: {
    channelId?: string;
    uid?: string;
    userType?: string;
  };
}

export const getConfig = async (params: IConfigInfo): Promise<IRes<Option>> => {
  const res = await axios.post('/user/getNotice', params);
  return res.data;
};

export interface OrderListParams {
  /** 用户类型：1-司机，2-小B，3-企业，4-其他，5-无忧搬家小哥 */
  user_type?: number;
  user_id?: string;
  page_no?: number;
  page_size?: number;
  from?: string;
  /**
   * 此参数为游标，第一页传空数组即可，第二页由第一页返回值里的last_idstr_arr原样传入
   */
  last_idstr_arr?: unknown;
  channel_id?: number;
}

interface OrderListRes {
  order_list: OrderItem[];
  last_idstr_arr: unknown;
}

export const getOrderList = async (data: OrderListParams): Promise<IRes<OrderListRes>> => {
  const res = await axios.post('/order/getUserOrderList', data);
  return res.data;
};

export const getEvaluateConfig = async (channelId?: string): Promise<IRes<EvaluateConfig[]>> => {
  const res = await axios.post('/comment/config/getConfigs', { channelId });
  return res.data;
};

interface MessageListParams {
  uid?: string;
  lastMsgId?: string;
  size?: number;
  type?: string;
}

export const getMessageList = async (
  data: MessageListParams
): Promise<IRes<HistoryMessageContent[]>> => {
  const res = await axios.post('/user/getMessageList', data);
  return res.data;
};

interface UploadParams {
  file: File;
  uid?: string;
  channelId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUploadProgress?: (progressEvent: any) => void;
}

export const uploadFile = async (data: UploadParams) => {
  const formData = new FormData();
  formData.append('uid', data.uid as string);
  formData.append('channelId', data.channelId as string);
  formData.append('type', 'user');
  formData.append('file', data.file);
  return axios.post('/user/uploadFile', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: data.onUploadProgress
  });
};

export const getUserInfo = async (
  {
    user_type,
    token,
    open_id,
    from
  }: {
    user_type?: string;
    token?: string;
    open_id?: string;
    from?: string;
  },
  jwt?: string
): Promise<IRes<UserInfo>> => {
  if (open_id) {
    const res = await axios.get('/token/getOpenIdParsing', {
      params: { open_id },
      headers: { Authorization: `HLL_CSC_TOKEN${decodeURIComponent(jwt || '')}` }
    });
    return { ...res.data, data: res.data.data ? { ...res.data.data, open_id } : res.data.data };
  }

  const res = await axios.get('/token/parsingToken', { params: { token, user_type, from } });
  return res.data;
};

export const getCustomeHead = async (params: {
  uid?: string;
  identity?: string;
  user_id?: string;
  os?: string;
}): Promise<IRes<string>> => {
  const res = await axios.post('/user/getFace', null, { params });
  return res.data;
};

export const getBizGray = async (data: {
  channelId: string;
  cityId?: string;
}): Promise<IRes<GreyInfo>> => {
  const res = await axios.post('/comment/config/bizGray', data);
  return res.data;
};

export const asrTrackReport = async (data: AsrTrackInfo): Promise<IRes<null>> => {
  const res = await axios.post('/report/asrMessageEvent', data);
  return res.data;
};

export const checkSensitiveWords = async (content: string): Promise<IRes<SensitiveCheckResult>> => {
  const res = await axios.post('/sensitive/check', { content });
  return res.data;
};

export const getCityToChannelMap = async (data: {
  cityId?: string;
  channelId?: string;
}): Promise<IRes<{ channelId?: string }>> => {
  const res = await axios.post('/channel/forward', data);
  return res.data;
};

export const getAiConfig = async (channelId: string): Promise<IRes<AiConfig>> => {
  const res = await axios.get('channel/ai-config', { params: { channelId } });
  return res.data;
};

export const reportEvent = async (data: EventReportInfo): Promise<IRes<null>> => {
  const res = await axios.post('/event/report', data);
  return res.data;
};
